import React, { useState, useEffect } from 'react';
import { timeApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

export default function LaborVsEstimate({ projectId }) {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(true);

  useEffect(() => {
    let active = true;
    timeApi.laborVsEstimate(projectId)
      .then((r) => { if (active) setData(r.data); })
      .catch(() => { if (active) setShow(false); }); // 403 = bundle off, or no project
    return () => { active = false; };
  }, [projectId]);

  // Hide when the bundle is off, or there's neither a budget nor any logged labor
  if (!show || !data || (!data.has_budget && !data.has_logged)) return null;

  const basisHours = data.basis === 'hours';
  const pct = Math.min(data.percent_used || 0, 100);
  const over = data.over_budget;
  const barColor = over ? 'bg-red-500' : pct >= 85 ? 'bg-amber-500' : 'bg-green-500';

  return (
    <Card className={`mb-4 ${over ? 'border-red-300' : ''}`} data-testid="labor-vs-estimate">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="h-4 w-4 text-blue-700" /> Labor vs Estimate
          {over ? (
            <span className="ml-auto inline-flex items-center gap-1 text-red-600 text-sm font-medium">
              <AlertTriangle className="h-4 w-4" /> Over budget
            </span>
          ) : data.has_budget ? (
            <span className="ml-auto inline-flex items-center gap-1 text-green-600 text-sm font-medium">
              <CheckCircle2 className="h-4 w-4" /> On track
            </span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data.has_budget ? (
          <p className="text-sm text-muted-foreground" data-testid="labor-no-budget">
            No labor budget found on this job's estimate. You've logged{' '}
            <strong>{data.logged_hours} hrs</strong> of onsite labor
            {data.logged_dollars ? ` (${fmt(data.logged_dollars)})` : ''}.
          </p>
        ) : (
          <>
            <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
              <div className={`h-3 rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Logged</p>
                <p className="font-semibold" data-testid="labor-logged">
                  {data.logged_hours} hrs{data.logged_dollars ? ` · ${fmt(data.logged_dollars)}` : ''}
                </p>
              </div>
              <div className="text-right">
                <p className="text-muted-foreground text-xs">Estimate Budget</p>
                <p className="font-semibold" data-testid="labor-budget">
                  {basisHours ? `${data.budget_hours} hrs` : ''}
                  {basisHours && data.budget_dollars ? ' · ' : ''}
                  {data.budget_dollars ? fmt(data.budget_dollars) : (basisHours ? '' : '—')}
                </p>
              </div>
            </div>
            <p className={`text-sm font-medium ${over ? 'text-red-600' : 'text-muted-foreground'}`}>
              {data.percent_used}% of {basisHours ? 'budgeted hours' : 'labor budget'} used
              {basisHours && data.remaining_hours != null
                ? over
                  ? ` · ${Math.abs(data.remaining_hours)} hrs over`
                  : ` · ${data.remaining_hours} hrs remaining`
                : data.remaining_dollars != null
                  ? over
                    ? ` · ${fmt(Math.abs(data.remaining_dollars))} over`
                    : ` · ${fmt(data.remaining_dollars)} remaining`
                  : ''}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
